import messages from '../../constants/logMessages.json';
import { ApiError, ErrorResponseType } from '../../types/index';
import logging, { getMessage } from '../logging';

const { warnHeading, warnBold } = messages;

/**
 * Constructs a structured JSON string representing an API error response.
 *
 * @param {ApiError} mergedData - The API error object containing error details.
 * @param {ErrorResponseType} [errorResponse] - The optional error response, which may contain multiple error entries.
 * @returns {string} A JSON string representing the structured error response.
 */
export const apiErrorResponseConstructor = (
  mergedData: ApiError,
  errorResponse?: ErrorResponseType
): string => {
  // If no stack error is present, return a basic error response
  if (!errorResponse) {
    return JSON.stringify({
      errorCode: mergedData.data.errorCode,
      errorMessage: mergedData.data.message,
    });
  }

  // Ensure `errorResponse` contains an `errors` object; otherwise, log and return a basic error response
  if (!errorResponse.errors) {
    logging.info(
      getMessage('dtErrorResponse', ['errorResponse.errors']),
      warnHeading,
      warnBold
    );

    return JSON.stringify({
      errorCode: mergedData.data.errorCode,
      errorMessage: mergedData.data.message,
    });
  }

  // Ensure `errorResponse.errors` is treated as an array
  const errors = Array.isArray(errorResponse.errors)
    ? errorResponse.errors
    : [errorResponse.errors];

  // Construct the response by extracting and formatting relevant error details
  return JSON.stringify({
    errorCode: mergedData.data.errorCode, // API-specific error code
    errorMessage: mergedData.data.message, // Additional error message from the API response
    code: errors
      .map((e) => e.code ?? '')
      .filter(Boolean)
      .join(', '), // Concatenates error codes, filtering out empty values
    message: errors
      .map((e) => e.message ?? '')
      .filter(Boolean)
      .join(', '), // Concatenates error messages, filtering out empty values
    errorType: errors
      .map((e) => e.type ?? '')
      .filter(Boolean)
      .join(', '), // Concatenates error types, filtering out empty values
  });
};
